// CAPTURE'S ADDRESSES: the pages and sheets Capture links to, named once.
// Pure, so it is unit-tested without a browser (frontend/test/capture-page.test.cjs).
//
// The interview is a mode of the agent sidebar (owner, 2026-10-08,
// lib/seat.ts): its old addresses (/interview, /capture/interview,
// /capture/talk/<room>) open the sidebar in it (App.tsx).

import { seatHref } from './seat';
import { applyCardReturnTo } from './applyReturn';

/** YOUR NETWORK (owner call 2026-10-21: "rename references to network … it should encompass collaborators as well"):
 *  the people you know through Caliber, each a page where references pass both ways; the rail's NET. A reference stays
 *  the proof a person gives, and its own pages (/references/asked, /given, a person's file) keep their words. */
export const NETWORK_HREF = '/network';
/** Save someone's details first; asking them for a reference, or giving them one, is a later action on their page. */
export const ADD_PERSON_HREF = `${NETWORK_HREF}/new`;
/** A CARD'S ADD FROM YOUR NETWORK (its Network head's +): who to put on the card, a part of the card at /<card>/network/add. */
export const ADD_TO_CARD_HREF = `${NETWORK_HREF}/add`;
/** A FLOW'S CONTEXT RIDES THE JOURNEY (owner call 2026-10-30: "i hate seeing ?= url type bullshit"): asking someone to
 *  vouch, or giving them a reference, is a place (ASK_HREF, a person's …/give) and what it is about travels with the step
 *  that opened it, in the history entry's state (`flow`, its words as a query's), never the address. Opened afresh (a
 *  new tab, a reload of a bare link) it starts blank. Read where it lands by lib/flow useFlowSearch. */
export interface FlowLink { to: string; state: { flow: string } }
const flowLink = (to: string, q: URLSearchParams): FlowLink => ({ to, state: { flow: q.toString() } });
/** The ask's one place; who and what ride its flow. */
export const ASK_HREF = `${NETWORK_HREF}/ask`;
/** Giving a reference: under the person it is given from (their key), else the network's own. */
export const giveAt = (person?: string | null) => `${NETWORK_HREF}${person ? `/people/${encodeURIComponent(person)}` : ''}/give`;
/** ASKING SOMEONE TO VOUCH (owner call 2026-10-08): what it is about named by id (`ask`, once for each: a skill, a role,
 *  a certification, a project, an artifact), a skill not on your record by its name too; who it goes to (`to`, a
 *  person's node or their Caliber account, and that account's handle) when a person's file asks. */
export function askHref(about: readonly string[] = [], name?: string | null, to?: { id: string; username?: string } | null, returnTo?: string | null): FlowLink {
  const q = new URLSearchParams(about.length ? about.map((id) => ['ask', id]) : [['ask', '']]);
  if (name) q.set('name', name);
  if (to) q.set('to', to.id);
  if (to?.username) q.set('username', to.username);
  const from = applyCardReturnTo(returnTo);
  if (from) q.set('from', from);
  return flowLink(ASK_HREF, q);
}
/** GIVING A REFERENCE UNASKED (owner call 2026-10-09): who it goes to named when a person's page gives (`give`, their
 *  Caliber account, and `who`, their name), under that person (their key) as an ask from their page is. */
export function giveHref(to = '', who?: string, person?: string): FlowLink {
  const q = new URLSearchParams({ give: to });
  if (who) q.set('who', who);
  return flowLink(giveAt(person), q);
}
/** An ask said as one plain address, for what can carry nothing but a string (an agent's chip): the network with the
 *  ask's words, which lib/addressMoves moves into the ask's place and state as it opens. */
export const askAddress = (link: FlowLink) => `${NETWORK_HREF}?${link.state.flow}`;
/** A flow's link with its words as a query's: what an older address or a page's own builder (lib/referenceLayerModel)
 *  carries into the flow. */
export const flowTo = (to: string, q: URLSearchParams): FlowLink => flowLink(to, q);
export const NEW_REFERENCE_HREF = '/capture/references/new';

/** The interview: the agent sidebar opened in it, over the home (lib/seat.ts). */
export const interviewHref = () => seatHref('interview');

/** The role you aim at, your own words (owner call 2026-10-02: each out of
 *  Your details into its own block). The role you hold is Experience's
 *  open-ended one (owner call 2026-10-08): it has no sheet. */
const TARGET_HREF = '/capture/target';
const WORDS_HREF = '/capture/words';

/** The sheets Capture opens over the profile (pages/CapturePage). */
export type Sheet = 'people' | 'target' | 'words';

/** Which sheet an address opens over the profile, if any. */
export function sheetAt(pathname: string): Sheet | null {
  return pathname === TARGET_HREF ? 'target' : pathname === WORDS_HREF ? 'words'
        : pathname === NEW_REFERENCE_HREF ? 'people' : null;
}
